// src/pages/affiliate/PayoutsPage.tsx
// A partner's payouts — request one, watch its status move through the
// approval pipeline.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useAffiliate, useAffiliatePayout, useI18n } from '@react/hooks';
import { Button, Alert } from '@react/components';
import { authStore } from '@core/stores';
import type { Affiliate, Payout } from '@core/types/affiliate';
import { PayoutRequestDialog } from '@/features/affiliate/PayoutRequestDialog';
import { PayoutStatusBadge } from '@/features/affiliate/badges';
import { errMessage, fmtDate, formatMoney, getStoredAffiliateId, listParams } from '@/features/affiliate/format';

export const PayoutsPage = observer(() => {
  const { t, locale } = useI18n();
  const store = useAffiliatePayout();
  const affiliateStore = useAffiliate();
  const profileId = authStore.activeProfileId;
  const myAffiliateId = getStoredAffiliateId(profileId);

  const [affiliate, setAffiliate] = useState<Affiliate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [requestOpen, setRequestOpen] = useState(false);
  const [selected, setSelected] = useState<Payout | null>(null);

  const load = () => {
    if (!myAffiliateId) return;
    setError(null);
    store
      .ListPayouts(listParams({ limit: 100 }, { affiliate_id: myAffiliateId }))
      .catch((e: unknown) => setError(errMessage(e, t('affiliate.payouts.loadError'))));
  };

  useEffect(() => {
    load();
    if (myAffiliateId) affiliateStore.GetAffiliate(myAffiliateId).then(setAffiliate).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myAffiliateId]);

  if (!myAffiliateId) {
    return (
      <div className="p-6">
        <Alert variant="info">{t('affiliate.dashboard.onboardHint')}</Alert>
        <Link to="/affiliate" className="mt-3 inline-block text-sm text-primary hover:underline">
          {t('affiliate.backToDashboard')}
        </Link>
      </div>
    );
  }

  return (
    <div className="flex h-full">
      <div className="min-w-0 flex-1 space-y-4 overflow-y-auto p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          {/* The tab already names this page; the heading stays for screen readers. */}
          <h1 className="sr-only">{t('affiliate.payouts.title')}</h1>
          {affiliate && (
            <Button className="ml-auto" size="sm" onClick={() => setRequestOpen(true)}>
              {t('affiliate.payouts.request')}
            </Button>
          )}
        </div>

        {error && <Alert variant="error">{error}</Alert>}

        {store.isLoading && store.items.length === 0 && (
          <div className="space-y-2" aria-busy="true">
            <div className="h-14 animate-pulse rounded-md bg-muted" />
            <div className="h-14 animate-pulse rounded-md bg-muted" />
          </div>
        )}

        {!store.isLoading && store.items.length === 0 && !error && (
          <div className="rounded-md border border-dashed border-border p-8 text-center">
            <p className="text-sm text-muted-foreground">{t('affiliate.payouts.empty')}</p>
            <p className="mt-1 text-sm text-muted-foreground">{t('affiliate.payouts.subtitle')}</p>
          </div>
        )}

        <div className="space-y-2">
          {store.items.map((p) => (
            <button
              key={p.entity?.id}
              onClick={async () => {
                // Load the full payout — the list payload may be trimmed.
                let full = p;
                if (p.entity?.id) {
                  try { full = await store.GetPayout(p.entity.id); } catch { full = p; }
                }
                setSelected(full);
              }}
              className={`w-full rounded-md border p-3 text-left hover:bg-muted ${selected?.entity?.id === p.entity?.id ? 'border-primary' : 'border-border'}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-foreground">{formatMoney(p.amount, locale)}</span>
                <PayoutStatusBadge status={p.status} />
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{fmtDate(p.created_at, locale)}</p>
            </button>
          ))}
        </div>
      </div>

      {selected && (
        <aside className="w-full shrink-0 space-y-3 overflow-y-auto border-l border-border p-4 lg:w-80">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground">{t('affiliate.payouts.detailTitle')}</h2>
            <button onClick={() => setSelected(null)} aria-label={t('common.close')} className="rounded-md p-1 text-muted-foreground hover:bg-muted">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold text-foreground">{formatMoney(selected.amount, locale)}</span>
            <PayoutStatusBadge status={selected.status} />
          </div>
          <dl className="space-y-1.5 text-sm">
            <div className="flex justify-between"><dt className="text-muted-foreground">{t('affiliate.payouts.requested')}</dt><dd className="text-foreground">{fmtDate(selected.created_at, locale)}</dd></div>
            {selected.approved_at && <div className="flex justify-between"><dt className="text-muted-foreground">{t('affiliate.payouts.approved')}</dt><dd className="text-foreground">{fmtDate(selected.approved_at, locale)}</dd></div>}
            {selected.paid_at && <div className="flex justify-between"><dt className="text-muted-foreground">{t('affiliate.payouts.paid')}</dt><dd className="text-foreground">{fmtDate(selected.paid_at, locale)}</dd></div>}
            <div className="flex justify-between"><dt className="text-muted-foreground">{t('affiliate.payouts.conversionsCount')}</dt><dd className="text-foreground">{selected.conversions_count}</dd></div>
          </dl>
          {selected.reject_reason && <Alert variant="warning">{selected.reject_reason}</Alert>}
        </aside>
      )}

      {affiliate && (
        <PayoutRequestDialog
          open={requestOpen}
          onClose={() => setRequestOpen(false)}
          affiliate={affiliate}
          onRequested={load}
        />
      )}
    </div>
  );
});
